import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { styled, vh, darkTheme } from "~/ui/style/stitches.config";
import { HistoryPuzzle, getPuzzleHistory } from "~/api/gameApi";
import { TarotCard } from "~/api/communityApi";
import { imageUrl } from "~/utils/imageurl";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import {
  RulesOverlay,
  RulesModal,
  ShowKeywordsToggle,
  AnswerKeywords,
} from "./GamePage";

export const GameHistoryModal = ({ allCards, onClose }: { allCards: TarotCard[]; onClose: () => void }) => {
  const [items, setItems] = useState<HistoryPuzzle[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showKeywords, setShowKeywords] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    getPuzzleHistory()
      .then((res) => {
        if (ignore) return;
        setItems(res.items);
        setCursor(res.nextCursor);
        setHasMore(!!res.nextCursor);
      })
      .catch((err) => console.error("Failed to load puzzle history:", err))
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, []);

  const loadMore = () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    getPuzzleHistory(cursor)
      .then((res) => {
        setItems((prev) => [...prev, ...res.items]);
        setCursor(res.nextCursor);
        setHasMore(!!res.nextCursor);
      })
      .catch((err) => console.error("Failed to load more puzzle history:", err))
      .finally(() => setLoadingMore(false));
  };

  return (
    <>
      <RulesOverlay
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <RulesModal
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <GameHistoryCard>
          <img
            className="x-button"
            onClick={onClose}
            src="/images/exit.svg"
            alt="Close"
            style={{
              position: "absolute",
              top: "16px",
              right: "12px",
              width: "28px",
              height: "28px",
              cursor: "pointer",
              opacity: 1,
              transition: "opacity 0.2s ease",
              userSelect: "none",
              zIndex: 1,
            }}
            onMouseOver={(e) => (e.currentTarget.style.opacity = "0.6")}
            onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
          />
          <GameHistoryScroll>
            <GameHistoryHeader>
              <GameHistoryTitleRow>
                <GameHistoryTitle>Your puzzle history</GameHistoryTitle>
              </GameHistoryTitleRow>
              <ShowKeywordsToggle
                css={{ display: "block", margin: "4px auto 3px" }}
                onClick={() => { setShowKeywords((s) => !s); }}
              >
                {showKeywords ? "hide keywords" : "show keywords"}
              </ShowKeywordsToggle>
            </GameHistoryHeader>
            {loading ? (
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                <SimpleLoadingIndicator style={{ width: "24px", height: "24px", position: "static", marginTop: 0, opacity: 0.4 }} />
              </div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
              {items.map((item) => {
                const c1 = allCards.find((c) => c.name === item.puzzle.answer.card_1.name);
                const c2 = allCards.find((c) => c.name === item.puzzle.answer.card_2.name);
                return (
                  <GameHistoryItem key={item.id}>
                    <GameHistoryPhrase>{item.puzzle.phrase}</GameHistoryPhrase>
                    <GameHistoryCardRow>
                      <GameHistoryCardCol>
                        <GameHistoryCardSlot>
                          {c1?.imagePath && (
                            <GameHistoryCardImage src={imageUrl(c1.imagePath, 200)} alt={item.puzzle.answer.card_1.name} />
                          )}
                        </GameHistoryCardSlot>
                        <GameHistoryCardText>
                          <GameHistoryCardName>{item.puzzle.answer.card_1.name}</GameHistoryCardName>
                          {showKeywords && c1?.keywords && c1.keywords.length > 0 && (
                            <AnswerKeywords css={{ maxWidth: "none", marginTop: "1px", paddingLeft: "8px", paddingRight: "8px" }}>{c1.keywords.join(" • ")}</AnswerKeywords>
                          )}
                        </GameHistoryCardText>
                      </GameHistoryCardCol>
                      <GameHistoryCardCol>
                        <GameHistoryCardSlot>
                          {c2?.imagePath && (
                            <GameHistoryCardImage src={imageUrl(c2.imagePath, 200)} alt={item.puzzle.answer.card_2.name} />
                          )}
                        </GameHistoryCardSlot>
                        <GameHistoryCardText>
                          <GameHistoryCardName>{item.puzzle.answer.card_2.name}</GameHistoryCardName>
                          {showKeywords && c2?.keywords && c2.keywords.length > 0 && (
                            <AnswerKeywords css={{ maxWidth: "none", marginTop: "1px", paddingLeft: "10px", paddingRight: "10px" }}>{c2.keywords.join(" • ")}</AnswerKeywords>
                          )}
                        </GameHistoryCardText>
                      </GameHistoryCardCol>
                    </GameHistoryCardRow>
                  </GameHistoryItem>
                );
              })}
              </motion.div>
            )}
            {!loading && hasMore && !loadingMore && (
              <ShowMoreContainer>
                <ShowMoreButton onClick={() => loadMore()}>View more</ShowMoreButton>
              </ShowMoreContainer>
            )}
            {loadingMore && (
              <LoadingMore>
                <SimpleLoadingIndicator style={{ width: "20px", height: "20px", position: "static", top: 0, opacity: 0.5 }} />
              </LoadingMore>
            )}
          </GameHistoryScroll>
        </GameHistoryCard>
      </RulesModal>
    </>
  );
};


const GameHistoryCard = styled("div", {
  position: "relative",
  width: "92%",
  maxWidth: "420px",
  height: `min(640px, ${vh(85)})`,
  background: "$wash",
  [`.${darkTheme.className} &`]: { background: "$surface", "& .x-button": { filter: "invert(1)" } },
  borderRadius: "24px",
  padding: "0 24px",
  border: "1px solid $border",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",

  "@mobile": {
    border: "none !important",
    borderRadius: 0,
    width: "100%",
    height: "100%",
    maxWidth: "none",
    maxHeight: "none !important",
    minHeight: `calc(${vh(100)} - 140px)`,
    margin: 0,
  },
});

const GameHistoryHeader = styled("div", {
  paddingTop: "44px",
  paddingBottom: "16px",
  "@mobile": {
    paddingTop: "52px",
  },
});

const GameHistoryTitleRow = styled("div", {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const GameHistoryTitle = styled("h2", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "27px",
  fontWeight: 400,
  textAlign: "center",
  width: "100%",
  margin: 0,
  color: "$text",
});

const GameHistoryScroll = styled("div", {
  overflowY: "auto",
  scrollbarGutter: "stable",
  paddingRight: "24px",
  paddingBottom: "40px",
  "@mobile": {
    paddingBottom: "100px",
  },
  marginRight: "-24px",
  flex: 1,
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "6px",
    backgroundColor: "rgba(127, 127, 127, .5)",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
    marginTop: "20px",
    marginBottom: "20px",
  },
  "&::-webkit-scrollbar-corner": {
    backgroundColor: "transparent",
  },
});

const GameHistoryItem = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "10px",
  padding: "20px 0 12px",
  marginBottom: "20px",
  "&:last-child": {
    marginBottom: 0,
  },
});

const GameHistoryPhrase = styled("p", {
  textAlign: "center",
  textWrap: "balance",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.3,
  color: "$text",
  margin: 0,
  paddingLeft: "10px",
  paddingRight: "10px",
});

const GameHistoryCardRow = styled("div", {
  display: "flex",
  flexDirection: "row",
  gap: "8px",
});

const GameHistoryCardCol = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "6px",
  padding: "14px 10px",
  background: "$gray200",
  [`.${darkTheme.className} &`]: { background: "$subtleFill" },
  borderRadius: "12px",
});

const GameHistoryCardSlot = styled("div", {
  width: "50px",
  borderRadius: "4px",
  border: "1px solid $gray300",
  [`.${darkTheme.className} &`]: { borderColor: "$inputBorder" },
  background: "transparent",
  overflow: "hidden",
  display: "block",
  fontSize: 0,
  flexShrink: 0,
});

const GameHistoryCardImage = styled("img", {
  display: "block",
  width: "100%",
  height: "auto",
  borderRadius: "3px",
});

const GameHistoryCardText = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minWidth: 0,
  width: "130px",
});

const GameHistoryCardName = styled("div", {
  fontSize: "11px",
  fontWeight: 500,
  color: "$gray800",
  [`.${darkTheme.className} &`]: { color: "$text" },
  textAlign: "center",
  lineHeight: 1.3,
});


const ShowMoreContainer = styled("div", {
  display: "flex",
  justifyContent: "center",
  marginTop: "24px",
  marginBottom: "10px",
});

const ShowMoreButton = styled("button", {
  padding: "9px 20px",
  fontSize: "11px",
  border: "none",
  borderRadius: "20px",
  backgroundColor: "#e4e4e4",
  color: "$text",
  cursor: "pointer",
  transition: "all 0.2s ease",
  [`.${darkTheme.className} &`]: { backgroundColor: "$subtleFill" },
  "&:hover": {
    backgroundColor: "$inverseSurface",
    color: "$inverseText",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "$inverseSurface",
    },
  },
});

const LoadingMore = styled("div", {
  display: "flex",
  justifyContent: "center",
  marginTop: "24px",
});
